// Generic pre-read preview — picks the right backend endpoint based on
// `kind` and lets the Strategist fill in the qualitative inputs that the
// system can't compute (asks, theme, recommendation reasoning).

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, type PrereadDoc } from "../lib/api";

type PrereadKind = "ceo-retro" | "champion-forum" | "90day";

export function PrereadPreview({ kind }: { kind: PrereadKind }) {
  const [scheduled, setScheduled] = useState<string>(in24h());
  const [theme, setTheme] = useState<string>("");
  const [asksText, setAsksText] = useState<string>("");
  const [cohortLabel, setCohortLabel] = useState<string>("Q1-pilot");
  const [path, setPath] = useState<"A" | "B">("A");
  const [label, setLabel] = useState<string>("Rollout");
  const [reasoning, setReasoning] = useState<string>("");
  const [risksText, setRisksText] = useState<string>("");
  const [questionsText, setQuestionsText] = useState<string>("");

  const queryKey = [kind, scheduled, theme, asksText, cohortLabel, path, label, reasoning, risksText, questionsText];

  const fetchPreread = async (): Promise<PrereadDoc> => {
    if (kind === "ceo-retro") {
      return api.preread.ceoRetro(scheduled, splitLines(asksText));
    }
    if (kind === "champion-forum") {
      return api.preread.championForum(scheduled, theme);
    }
    return api.preread.ninetyDay({
      cohort_label: cohortLabel,
      path, label, reasoning,
      risks: splitLines(risksText),
      questions: splitLines(questionsText),
    });
  };

  const q = useQuery<PrereadDoc>({ queryKey, queryFn: fetchPreread });

  const send = useMutation({
    mutationFn: async () => {
      // Re-use the same query params on the send endpoint.
      const params = new URLSearchParams();
      if (kind === "ceo-retro") {
        if (scheduled) params.set("scheduled_for", scheduled);
        if (asksText) params.set("asks", splitLines(asksText).join("|"));
      } else if (kind === "champion-forum") {
        if (scheduled) params.set("scheduled_for", scheduled);
        if (theme) params.set("theme", theme);
      }

      const url = `/api/admin/preread/${kind}/send?${params.toString()}`;
      const body = kind === "90day"
        ? JSON.stringify({
            cohort_label: cohortLabel, path, label, reasoning,
            risks: splitLines(risksText),
            questions: splitLines(questionsText),
          })
        : undefined;
      const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body,
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: (data: any) => alert(`Status: ${data.status} · dedup_key=${data.dedup_key}`),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{titleFor(kind)} pre-read</h1>

      <div className="border rounded p-3 bg-white space-y-2">
        {kind !== "90day" ? (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">Scheduled for</label>
              <input
                type="datetime-local"
                className="w-full border rounded px-2 py-1 text-sm"
                value={scheduled.replace("T", "T")}
                onChange={(e) => setScheduled(e.target.value)}
              />
            </div>
            {kind === "champion-forum" ? (
              <div>
                <label className="text-xs text-slate-600">Pattern theme (one sentence)</label>
                <input
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder="e.g. Three functions independently asking for Tier-3 access"
                />
              </div>
            ) : null}
            {kind === "ceo-retro" ? (
              <div className="col-span-2">
                <label className="text-xs text-slate-600">Strategist's two asks (one per line)</label>
                <textarea
                  rows={3}
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={asksText}
                  onChange={(e) => setAsksText(e.target.value)}
                  placeholder="Unblock Tier-3 access for Engineering\nDecide on Champion-track offers"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Input label="Cohort label" value={cohortLabel} onChange={setCohortLabel} />
            <div>
              <label className="text-xs text-slate-600">Path</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={path} onChange={(e) => setPath(e.target.value as "A" | "B")}>
                <option value="A">A — Rollout</option>
                <option value="B">B — Foundation extension</option>
              </select>
            </div>
            <Input label="Path label" value={label} onChange={setLabel} />
            <Input label="Reasoning (one paragraph)" value={reasoning} onChange={setReasoning} long />
            <TextArea label="Top 3 risks (one per line)" value={risksText} onChange={setRisksText} />
            <TextArea label="Three open questions (one per line)" value={questionsText} onChange={setQuestionsText} />
          </div>
        )}
        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-slate-600">
            {q.data ? (
              <>Recipients: {q.data.recipients.join(", ") || <span className="text-amber-700">none configured</span>}</>
            ) : null}
          </div>
          <button
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
            disabled={send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? "Sending…" : "Enqueue"}
          </button>
        </div>
      </div>

      {q.isLoading ? <p>Rendering…</p> : null}
      {q.error ? <p className="text-red-700 text-sm">{(q.error as Error).message}</p> : null}
      {q.data ? (
        <div className="border rounded bg-white">
          <div className="px-3 py-2 border-b text-sm">
            <b>Subject:</b> {q.data.subject}
          </div>
          <iframe
            title="preread-preview"
            srcDoc={q.data.body_html}
            className="w-full"
            style={{ height: "70vh", border: 0 }}
          />
        </div>
      ) : null}
    </div>
  );
}

function titleFor(k: PrereadKind) {
  switch (k) {
    case "ceo-retro":      return "CEO retro";
    case "champion-forum": return "Champion forum";
    case "90day":          return "90-day review";
  }
}

function in24h(): string {
  const d = new Date(Date.now() + 24 * 3600_000);
  d.setSeconds(0, 0);
  return d.toISOString().slice(0, 16);
}

function splitLines(s: string): string[] {
  return s.split("\n").map((l) => l.trim()).filter(Boolean);
}

function Input({ label, value, onChange, long }: { label: string; value: string; onChange: (v: string) => void; long?: boolean }) {
  return (
    <div className={long ? "col-span-2" : ""}>
      <label className="text-xs text-slate-600">{label}</label>
      <input className="w-full border rounded px-2 py-1 text-sm" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="col-span-2">
      <label className="text-xs text-slate-600">{label}</label>
      <textarea rows={3} className="w-full border rounded px-2 py-1 text-sm" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
